// medplum-patients.js — ONLINE-PRIMARY, same recipe as queue/reception.
// Patients.jsx admits both CLINICAL and front-desk roles; either reads as the
// ONE logged-in session — RLS scopes what each sees.
//
// PAGINATION (2026-07-06): the directory is SERVER-SIDE PAGED. The old query
// (Patient?_revinclude=Encounter:subject&_count=1000) pulled EVERY patient in
// one payload — measured 913 KB / 865 entries for 853 patients. We now fetch
// ONE page of 20 patients at a time (~21 KB) with an accurate total, and let
// the visible page's own encounters ride in via _revinclude (which, on a paged
// Patient query, only pulls the 20 shown patients' encounters — measured
// ~21 KB total, a single round-trip, marginally CHEAPER than a separate batch
// Encounter fetch, so we keep it to one query).
//
// Three coherent scopes, each a distinct server query (see Patients.jsx ScopeToggle):
//   • 'all'    → every patient, PAGED (Patient?_count=20&_offset=N&_sort=name).
//   • 'mine'   → "My department" = patients who have an Encounter served by MY
//                department Organization, PAGED exactly like 'all'.
//   • 'access' → "My access" = patients I can reach WITHOUT my department —
//                a grant (the access-grant Bot stamps my %profile compartment),
//                being the treating practitioner, or the shared emergency
//                surface. Restored 2026-08-04 (DEFERRED §59); see below.
//
// ── WHY 'access' IS AN ENCOUNTER QUERY AND NOT A `_has` ON PATIENT ───────────
// The obvious formulation —
//   Patient?_has:Encounter:subject:service-provider:not=Organization/<myDept>
// — parses and runs, but it is WRONG, and dangerously so: Medplum applies the
// caller's AccessPolicy ONLY to the OUTER searched resource. `getBaseSelectQuery`
// calls `repo.addSecurityFilters(builder, resourceType, …)` once for the searched
// type (medplum-link/packages/server/src/fhir/search.ts ~L444), while a chained
// `_has` becomes a bare `EXISTS(subquery)` over the reference lookup tables with
// NO security filter (`buildChainedSearchUsingReferenceTable`, same file ~L1704).
// Measured live against the local sandbox as a Cardiology doctor (2026-08-04):
//   Encounter?service-provider=Organization/<neurology>              → total 0
//   Patient?_has:Encounter:subject:service-provider=Organization/<neurology> → total 12
// i.e. the reverse chain "sees" encounters the caller cannot read, so a `:not`
// version returned 654 patients where the caller can genuinely reach 4.
//
// So 'access' asks the DATABASE what it may read, by searching the type RLS
// actually filters — Encounter — with the department excluded server-side:
//   Encounter?service-provider:not=Organization/<myDept>
//            &_filter=not (subject:Patient._has:Encounter:subject:service-provider
//                          eq Organization/<myDept>)
//            &_include=Encounter:subject&_sort=-date&_count=1000&_total=accurate
// The `service-provider:not` leg is the RLS-filtered match set (only encounters
// I may read, none of them my department's). The `_filter` leg drops patients
// who ALSO have a department encounter — they are 'department' rows and belong
// on the "My department" pill, not here; the negative reverse-chain is sound
// even though it ignores RLS, because "has a department encounter" and "has a
// READABLE department encounter" are the same set (that IS the policy arm).
// `_include=Encounter:subject` returns those patients in the SAME round-trip.
// Nothing about the policy is duplicated client-side, so this stays correct for
// every role (doctor / intern / nurse / receptionist) and survives policy edits.
//
// This scope is deliberately NOT paged (`paged:false`, the shape the directory
// already supported for a bounded set): it is the small, exceptional set by
// construction, we load ALL of it, and the count we show is therefore the exact
// count. It is capped at ACCESS_SCOPE_MAX_ENCOUNTERS (1000 — Medplum's own
// server-side `_count` ceiling, measured) and FAILS LOUD past that rather than
// quietly showing a partial list under a confident number.
//
// FUNCTIONAL DEPT FILTER, NOT an RLS assumption (2026-07-07 fix): the earlier
// "'mine' = Encounter?_count=1000&_include=Encounter:subject, RLS-bounded to my
// dept" was WRONG on the cloud demo, where doctors bind the broad main-1
// `opd-doctor` AccessPolicy (no `%department` criterion) — so that query
// returned EVERY department's patients (the "ortho patients for a non-ortho
// doctor" bug) and never paginated. We now filter EXPLICITLY server-side:
//   Patient?_has:Encounter:subject:service-provider=Organization/<myDeptId>
// i.e. "patients who have an Encounter whose service-provider is my department
// Organization". This is CORRECT regardless of how broad the bound AccessPolicy
// is — the reverse-chain is the functional department filter; RLS remains the
// independent security lock (this is scoping a query, NOT an app-side security
// re-check — see AGENTS.md §2). The dept Organization id is resolved from the
// logged-in user's derived department slug (deriveCurrentUser) via a one-time
// Organization?identifier=<slug> lookup. A `_has` on a paged Patient query pages
// PATIENTS (not encounters), so "of N" counts distinct department patients and a
// patient with several dept visits still occupies exactly one row on one page.
//
// SEARCH is server-side too. In 'all' it spans every patient; in 'mine' it is
// ANDed with the department reverse-chain (both are Patient params) so search
// stays scoped to the department. Either way it is PAGED and resets to page 1.
import { getMedplum, deriveCurrentUser } from './client-port.js';
import { groupPatientBundle, shapePatientRow } from '@healther/clinical-core/shape/patients-view.js';

export const PATIENTS_PAGE_SIZE = 20;
const DEPT_SLUG_SYSTEM = 'https://healther.io/opd/department-slug';
// Medplum clamps `_count` to 1000 server-side (measured: `_count=5000` returns
// 1000 entries). The 'access' scope loads its whole set in one query, so this is
// both the page size and the honest ceiling — past it we throw (see below).
const ACCESS_SCOPE_MAX_ENCOUNTERS = 1000;

// Resolves the logged-in user's department Organization id from their derived
// department SLUG (deriveCurrentUser). Every staff AccessPolicy grants
// Organization read (no criteria), so this one lookup is always allowed.
// Returns null when the user has no department (owner/admin/front-desk) — the
// caller then has no 'mine' set to show. Memoized per session (the slug→id
// mapping is stable for the life of the login).
let deptOrgIdCache = null; // { slug, id }
async function resolveDepartmentOrgId(medplum) {
  const user = await deriveCurrentUser(medplum);
  const slug = user && user.department;
  if (!slug) return null;
  if (deptOrgIdCache && deptOrgIdCache.slug === slug) return deptOrgIdCache.id;
  const org = await medplum.searchOne(
    'Organization', `identifier=${DEPT_SLUG_SYSTEM}|${encodeURIComponent(slug)}`);
  const id = (org && org.id) || null;
  deptOrgIdCache = { slug, id };
  return id;
}

// Escapes a user search term for a FHIR _filter string literal (double-quote
// delimited) — backslash and double-quote are the only metacharacters.
const filterLiteral = (s) => String(s).replace(/\\/g, '\\\\').replace(/"/g, '\\"');

// Builds the _filter OR expression covering name / phone / MRN. Names and
// phones use "co" (contains). MRN uses "eq": on this Medplum, `identifier co`
// is a DEAD branch — token params don't do substring matching, so `co` returns
// zero for every MRN while `eq` matches the full value (measured live on the
// 265-patient rig 2026-07-11; same root cause as the family-panel search fix).
function searchFilter(term) {
  const lit = filterLiteral(term.trim());
  return `name co "${lit}" or telecom co "${lit}" or identifier eq "${lit}"`;
}

// The SAME three-way search, expressed from an Encounter search by forward-
// chaining onto the subject Patient — used by the 'access' scope, whose match
// set is Encounters. Medplum parses `subject:Patient.<param>` inside a `_filter`
// comparison exactly like a top-level chained parameter (search.ts
// buildFilterParameterComparison → buildSearchFilterExpression → isChainedSearchFilter);
// verified live 2026-08-04.
function encounterSubjectSearchFilter(term) {
  const lit = filterLiteral(term.trim());
  return `subject:Patient.name co "${lit}" or subject:Patient.telecom co "${lit}"`
    + ` or subject:Patient.identifier eq "${lit}"`;
}

/**
 * Loads ONE page of the patient directory as the logged-in user.
 *
 * @param {object}  opts
 * @param {number}  opts.page      1-based page number (default 1).
 * @param {number}  opts.pageSize  rows per page (default PATIENTS_PAGE_SIZE).
 * @param {string}  opts.search    free-text: name / partial phone / MRN.
 * @param {string}  opts.scope     'all' | 'mine' | 'access' (see block comment).
 *
 * Returns the same DTO shape the screen already consumes, now carrying paging
 * meta so a cached page-1 render is self-describing:
 *   { source, patients: { rows, counts, page, pageSize, total, paged },
 *     currentUser, hospital }
 * `paged` is true for 'all' and 'mine' — both are server-paged the same way
 * (same _count/_offset/_total/_sort/_revinclude), so the pager renders
 * consistently across them (it self-hides when there is only one page). It is
 * false for 'access', which loads its whole bounded set in one query, so the
 * count shown is the exact count and there is nothing left to page.
 *
 * @throws when the 'access' set exceeds ACCESS_SCOPE_MAX_ENCOUNTERS — see the
 *   block comment. Failing loud beats an unpaged partial list under a
 *   confident-looking total (AGENTS §1.3).
 *
 * Bridge SWR calls this with no args → page 1, no search, scope defaulting to
 * 'all' (the widest coherent first paint; the screen then re-runs the loader
 * for the user's actual default scope). Page>1 and search are driven by the
 * screen calling this directly — only page 1 rides the SWR cache.
 */
export async function loadPatientsFromMedplum(opts = {}) {
  const { medplum, hospital } = await getMedplum();
  const page = Math.max(1, Number(opts.page) || 1);
  const pageSize = Number(opts.pageSize) || PATIENTS_PAGE_SIZE;
  const search = String(opts.search || '').trim();
  const scope = opts.scope || 'all';
  const deptScope = scope === 'mine';
  const accessScope = scope === 'access';

  // ── Resolve MY department Organization. ───────────────────────────────────
  // EVERY scope needs it now: 'mine' and 'access' filter on it server-side, and
  // 'all' needs it so each row's access tier (the "My department" / "Access"
  // chip) can be classified honestly — see shape/patients-view.js. Memoized per
  // session, so this is one Organization lookup per login, not per load.
  // If the user has no department (owner/admin/front-desk) the department-
  // relative scopes have no set to show → return an honest empty view rather
  // than silently widening to everyone. Under 'all' a null id simply means the
  // tier is undefined for that user, so no row claims a relationship it hasn't
  // got. (Empty rather than hiding the pill: the pill's count IS the honest
  // answer — "you reach nobody that way" — it needs no async plumbing into the
  // screen, and it is exactly how "My department" already behaves for them.)
  const deptOrgId = await resolveDepartmentOrgId(medplum);
  if ((deptScope || accessScope) && !deptOrgId) {
    return {
      source: 'medplum-client-shaping',
      patients: { rows: [], counts: { total: 0, withEncounters: null, withoutEncounters: null }, page: 1, pageSize, total: 0, paged: true, scope },
      currentUser: await deriveCurrentUser(medplum), hospital,
    };
  }

  // ── 'access' — ONE Encounter query, RLS-filtered, whole set, not paged ─────
  if (accessScope) {
    const deptRef = 'Organization/' + deptOrgId;
    const clauses = [`not (subject:Patient._has:Encounter:subject:service-provider eq ${deptRef})`];
    if (search) clauses.push('(' + encounterSubjectSearchFilter(search) + ')');
    const p = new URLSearchParams();
    p.set('_count', String(ACCESS_SCOPE_MAX_ENCOUNTERS));
    p.set('_total', 'accurate');
    p.set('_sort', '-date');
    p.set('_include', 'Encounter:subject');
    p.set('service-provider:not', deptRef);
    p.set('_filter', clauses.join(' and '));

    const bundle = await medplum.search('Encounter', p.toString());
    const entries = (bundle && bundle.entry) || [];
    const matched = entries.filter((e) => (e.resource && e.resource.resourceType === 'Encounter'));
    if (typeof bundle.total === 'number' && bundle.total > matched.length) {
      // We claim a complete, exactly-counted set; past the ceiling we cannot.
      throw new Error(`Patients "My access" scope exceeds ${ACCESS_SCOPE_MAX_ENCOUNTERS} `
        + `out-of-department encounters (${bundle.total}) — this view loads the whole set and `
        + 'cannot page it, so it would under-report. Narrow the access policy or page this scope.');
    }
    const patientsById = {};
    const encountersByPatient = {};
    for (const entry of entries) {
      const r = entry.resource; if (!r) continue;
      if (r.resourceType === 'Patient') patientsById[r.id] = r;
      else if (r.resourceType === 'Encounter') {
        const ref = r.subject && r.subject.reference;
        if (ref) (encountersByPatient[ref] = encountersByPatient[ref] || []).push(r);
      }
    }
    for (const encs of Object.values(encountersByPatient)) {
      encs.sort((a, b) => String((b.period && b.period.start) || '').localeCompare(String((a.period && a.period.start) || '')));
    }
    // These patients have NO department encounter (the _filter guarantees it),
    // so the included encounters ARE their whole readable set — the shaped row
    // (visit count, last visit, chip) is complete, not a slice.
    const rows = Object.values(patientsById)
      .map((pt) => shapePatientRow(pt, encountersByPatient['Patient/' + pt.id] || [], deptOrgId))
      .sort((a, b) => String(a.displayName || '').localeCompare(String(b.displayName || '')));
    return {
      source: 'medplum-client-shaping',
      patients: {
        rows,
        counts: { total: rows.length, withEncounters: null, withoutEncounters: null },
        page: 1, pageSize: rows.length || pageSize, total: rows.length, paged: false, scope,
      },
      currentUser: await deriveCurrentUser(medplum), hospital,
    };
  }

  // ── ONE server-side PAGED Patient query for 'all' and 'mine' ──────────────
  // One page of patients + only THIS page's encounters (fanned in via
  // _revinclude, which on a paged Patient query pulls just the shown patients'
  // encounters). _total=accurate gives the pager its "of N". 'mine' adds the
  // department reverse-chain so the same pagination, sort, and shaping apply
  // identically to both scopes — the ONLY difference is the extra filter.
  const params = new URLSearchParams();
  params.set('_count', String(pageSize));
  params.set('_offset', String((page - 1) * pageSize));
  params.set('_total', 'accurate');
  params.set('_sort', 'name');
  params.set('_revinclude', 'Encounter:subject');
  // Department filter (functional scoping, not a security re-check): patients
  // who have an Encounter served by my department Organization.
  if (deptScope) params.append('_has:Encounter:subject:service-provider', 'Organization/' + deptOrgId);
  // Search ANDs on top (in 'mine' it stays department-scoped; in 'all' it spans
  // every patient) — name / partial phone / MRN via one contains-OR _filter.
  if (search) params.set('_filter', searchFilter(search));

  const bundle = await medplum.search('Patient', params.toString());
  const total = typeof bundle.total === 'number' ? bundle.total : null;
  const { patients, encountersByPatient } = groupPatientBundle(bundle);
  const rows = patients.map((p) => shapePatientRow(p, encountersByPatient['Patient/' + p.id] || [], deptOrgId));

  // `total` is the real (accurate, from _total=accurate) size of the CURRENT
  // scope — every-patient for 'all', my-department for 'mine' — and is what the
  // pager + the "patients on file" KPI show. The with/without-encounter
  // sub-counts USED to be computed over a full ~1000-row payload; now that we
  // deliberately never load a whole scope, they're unknowable cheaply — so we
  // return null for BOTH scopes and the screen hides those two sub-tiles rather
  // than fabricate page-scoped numbers that would read as clinic-wide.
  const counts = {
    total: total != null ? total : rows.length,
    withEncounters: null,
    withoutEncounters: null,
  };

  // No generatedAt: the payload must be deterministic for a given data state —
  // the SWR layer diffs cached vs fresh by JSON equality, and a per-call
  // timestamp would make every revalidate look like a change.
  return {
    source: 'medplum-client-shaping',
    patients: { rows, counts, page, pageSize, total: total != null ? total : rows.length, paged: true, scope },
    currentUser: await deriveCurrentUser(medplum), hospital,
  };
}
